import type {SupportedCurrency, NetworkType} from 'src/v2/state/types';
import type {IntlHook} from '@sail/intl';
import type {SupportedNetwork} from '@stripe-internal/wallet-connect-wrapper';
import {EVM_NETWORKS} from 'src/types';
import {IconAsset} from '@sail/icons/types';
import {getNetworkIcon, getNetworkName} from 'src/components/Wallet/constants';
import {sortNetworksTempoFirst} from 'src/v2/util/sortNetworksTempoFirst';

export type NetworkInfo = {
  networkType: NetworkType;
  label: string;
  icons: Array<{icon: IconAsset; name: string}>;
};

const getNetworkInfo = (network: SupportedNetwork) => ({
  icon: getNetworkIcon(network),
  name: network,
  displayName: getNetworkName(network),
});

export const buildNetworksFromSupportedCurrencies = (
  intl: IntlHook,
  supportedCurrencies: SupportedCurrency[],
): NetworkInfo[] => {
  // Extract unique networks from supported currencies
  const supportedNetworks = [
    ...new Set(supportedCurrencies.map((currency) => currency.currencyNetwork)),
  ];

  const supportedEvmNetworks = sortNetworksTempoFirst(
    supportedNetworks.filter((network) =>
      EVM_NETWORKS.includes(network),
    ) as SupportedNetwork[],
  );

  const supportsSolana = supportedNetworks.some(
    (network) => network === 'solana',
  );

  // Build networks list dynamically
  const networks: NetworkInfo[] = [];

  if (supportedEvmNetworks.length > 0) {
    const evmDisplayNames = supportedEvmNetworks
      .map((network) => getNetworkInfo(network).displayName)
      .filter(Boolean);

    networks.push({
      networkType: 'evm',
      label: intl.formatList(evmDisplayNames),
      icons: supportedEvmNetworks
        .map((network) => getNetworkInfo(network))
        .filter(Boolean),
    });
  }

  if (supportsSolana) {
    const solanaInfo = getNetworkInfo('solana');
    networks.push({
      networkType: 'solana',
      label: solanaInfo.displayName,
      icons: [solanaInfo],
    });
  }

  return networks;
};
